/**
 * Workspace file handlers — upload, list, read workspace .md files.
 *
 * Workspace files (SOUL.md, IDENTITY.md, etc.) define agent personality
 * and behavior. These handlers validate filenames, verify signatures,
 * enforce size limits, and perform atomic writes.
 */

import { readdir, readFile, writeFile, rename, stat, unlink, mkdir } from "node:fs/promises";
import { join } from "node:path";
import { randomUUID } from "node:crypto";
import { verifyDocumentSignature, base64ToBytes } from "@agentvault/crypto";

export interface WorkspaceFileInfo {
  filename: string;
  size: number;
  modified: string; // ISO 8601
}

export interface WorkspaceUploadData {
  filename: string;
  content: string;
  signature: string;       // base64 Ed25519 signature
  signer_public_key: string; // base64 Ed25519 public key
  timestamp: string;       // ISO 8601
}

export interface WorkspaceUploadResult {
  status: "success" | "error";
  error?: string;
  written_path?: string;
  verified_signature?: boolean;
}

export interface WorkspaceListResult {
  files: WorkspaceFileInfo[];
}

export interface WorkspaceReadResult {
  filename: string;
  content: string;
  size: number;
}

export interface WorkspaceReadError {
  error: string;
}

/** Maximum file size: 100 KB */
const MAX_FILE_SIZE = 100 * 1024;

/**
 * Validate a workspace filename.
 * Returns an error message if invalid, null if valid.
 *
 * Rules:
 * - Must end in .md
 * - No path traversal (.., /)
 * - No backslashes or null bytes
 * - Alphanumeric, hyphens, underscores, and dots only
 */
export function validateWorkspaceFilename(filename: string): string | null {
  if (!filename || typeof filename !== "string") {
    return "Filename is required";
  }

  // Null byte check
  if (filename.includes("\0")) {
    return "Filename contains null bytes";
  }

  // Path traversal checks
  if (filename.includes("..")) {
    return "Path traversal not allowed";
  }
  if (filename.includes("/") || filename.includes("\\")) {
    return "Path separators not allowed in filename";
  }

  // Must end in .md
  if (!filename.endsWith(".md")) {
    return "Only .md files are allowed";
  }

  // Only allow alphanumeric, hyphens, underscores, dots
  if (!/^[a-zA-Z0-9._-]+$/.test(filename)) {
    return "Filename may only contain letters, numbers, hyphens, underscores, and dots";
  }

  return null;
}

/**
 * Handle a workspace file upload — verify signature, validate, write atomically.
 */
export async function handleWorkspaceUpload(
  data: WorkspaceUploadData,
  workspaceDir: string,
): Promise<WorkspaceUploadResult> {
  // Validate filename
  const filenameError = validateWorkspaceFilename(data.filename);
  if (filenameError) {
    return { status: "error", error: filenameError };
  }

  // Validate content
  if (!data.content || typeof data.content !== "string") {
    return { status: "error", error: "File content is required" };
  }

  // Size limit
  const contentBytes = Buffer.byteLength(data.content, "utf-8");
  if (contentBytes > MAX_FILE_SIZE) {
    return {
      status: "error",
      error: `File exceeds 100KB limit (${Math.round(contentBytes / 1024)}KB)`,
    };
  }

  // Verify Ed25519 signature
  let verified = false;
  try {
    const signatureBytes = base64ToBytes(data.signature);
    const publicKeyBytes = base64ToBytes(data.signer_public_key);
    const signedPayload = {
      filename: data.filename,
      content: data.content,
      timestamp: data.timestamp,
    };
    verified = await verifyDocumentSignature(signedPayload, signatureBytes, publicKeyBytes);
  } catch (err) {
    return { status: "error", error: "Signature verification failed: invalid format" };
  }

  if (!verified) {
    return { status: "error", error: "Invalid signature — file may have been tampered with" };
  }

  // Ensure workspace directory exists
  await mkdir(workspaceDir, { recursive: true });

  // Atomic write: write to temp, then rename
  const targetPath = join(workspaceDir, data.filename);
  const tempPath = join(workspaceDir, `.tmp_${randomUUID()}_${data.filename}`);

  try {
    await writeFile(tempPath, data.content, "utf-8");
    await rename(tempPath, targetPath);
  } catch (err: any) {
    // Clean up temp file if rename fails
    try {
      await unlink(tempPath);
    } catch {
      // ignore cleanup errors
    }
    return { status: "error", error: `Failed to write file: ${err.message}` };
  }

  console.log(`[Workspace] Wrote ${data.filename} (${contentBytes} bytes, sig verified) → ${targetPath}`);

  return {
    status: "success",
    written_path: targetPath,
    verified_signature: true,
  };
}

/**
 * List .md files in the workspace directory.
 */
export async function handleWorkspaceList(
  workspaceDir: string,
): Promise<WorkspaceListResult> {
  try {
    const entries = await readdir(workspaceDir);
    const files: WorkspaceFileInfo[] = [];

    for (const entry of entries) {
      if (!entry.endsWith(".md")) continue;

      try {
        const filePath = join(workspaceDir, entry);
        const fileStat = await stat(filePath);
        if (!fileStat.isFile()) continue;

        files.push({
          filename: entry,
          size: fileStat.size,
          modified: fileStat.mtime.toISOString(),
        });
      } catch {
        // Skip files we can't stat
      }
    }

    // Sort by filename
    files.sort((a, b) => a.filename.localeCompare(b.filename));

    return { files };
  } catch (err: any) {
    // If directory doesn't exist, return empty list
    if (err.code === "ENOENT") {
      return { files: [] };
    }
    throw err;
  }
}

/**
 * Read a workspace file's content.
 */
export async function handleWorkspaceRead(
  data: { filename: string },
  workspaceDir: string,
): Promise<WorkspaceReadResult | WorkspaceReadError> {
  // Validate filename
  const filenameError = validateWorkspaceFilename(data.filename);
  if (filenameError) {
    return { error: filenameError };
  }

  const filePath = join(workspaceDir, data.filename);

  try {
    const content = await readFile(filePath, "utf-8");
    const fileStat = await stat(filePath);

    return {
      filename: data.filename,
      content,
      size: fileStat.size,
    };
  } catch (err: any) {
    if (err.code === "ENOENT") {
      return { error: `File not found: ${data.filename}` };
    }
    return { error: `Failed to read file: ${err.message}` };
  }
}
